// src/components/frameitforward/GiftBoxCustomizeModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { CloseIcon } from '../../public/icons';
import reviewService from '../../services/reviewService';

const STEP_DRAFT_KEY = 'kapta_frameitforward_step_customize';

const loadStepDraft = () => {
  try {
    const raw = localStorage.getItem(STEP_DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    console.error('Failed to load customize draft:', err);
    return null;
  }
};

const saveStepDraft = (data) => {
  try {
    localStorage.setItem(STEP_DRAFT_KEY, JSON.stringify(data));
  } catch (err) {
    console.error('Failed to save customize draft:', err);
  }
};

const GiftBoxCustomizeModal = ({
  isOpen,
  onClose,
  onContinue,
  onProgressChange
}) => {
  const [selectedMagnetType, setSelectedMagnetType] = useState('upload-own');
  const [magnetSetCount, setMagnetSetCount] = useState(1);
  const [frameCount, setFrameCount] = useState(1);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [memoryTitle, setMemoryTitle] = useState('');
  const [memoryStory, setMemoryStory] = useState('');
  const [magnets, setMagnets] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const draftHydratedRef = useRef(false);

  /* ───────── HYDRATE on open ───────── */
  useEffect(() => {
    if (!isOpen) return;

    const draft = loadStepDraft();
    if (!draft) {
      draftHydratedRef.current = true;
      return;
    }

    if (typeof draft.selectedMagnetType === 'string')
      setSelectedMagnetType(draft.selectedMagnetType);
    if (typeof draft.magnetSetCount === 'number')
      setMagnetSetCount(draft.magnetSetCount);
    if (typeof draft.frameCount === 'number')
      setFrameCount(draft.frameCount);
    if (Array.isArray(draft.uploadedImages))
      setUploadedImages(draft.uploadedImages);
    if (typeof draft.memoryTitle === 'string')
      setMemoryTitle(draft.memoryTitle);
    if (typeof draft.memoryStory === 'string')
      setMemoryStory(draft.memoryStory);
    if (Array.isArray(draft.magnets)) setMagnets(draft.magnets);

    draftHydratedRef.current = true;
  }, [isOpen]);

  /* ───────── AUTOSAVE + broadcast on every change ───────── */
  useEffect(() => {
    if (!isOpen) return;
    if (!draftHydratedRef.current) return;

    const draft = {
      selectedMagnetType,
      magnetSetCount,
      frameCount,
      uploadedImages,
      memoryTitle,
      memoryStory,
      magnets
    };

    saveStepDraft(draft);
    onProgressChange?.(draft);
  }, [
    isOpen,
    selectedMagnetType,
    magnetSetCount,
    frameCount,
    uploadedImages,
    memoryTitle,
    memoryStory,
    magnets,
    onProgressChange
  ]);

  if (!isOpen) return null;

  /* ───────── Continue ───────── */
  const handleContinue = () => {
    const customizationData = {
      magnetType: selectedMagnetType,
      magnetSetCount: magnetSetCount,
      frameCount: frameCount,
      magnets: [...magnets]
    };
    onContinue(customizationData);
    onClose();
  };

  /* ───────── Upload to Cloudinary ───────── */
  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploadError('');
    setIsUploading(true);

    try {
      const uploaded = [];
      for (const file of files) {
        if (!file.type.startsWith('image/')) continue;
        if (file.size > 10 * 1024 * 1024) {
          setUploadError(`${file.name} exceeds 10MB and was skipped.`);
          continue;
        }
        const result = await reviewService.uploadImageToCloudinary(file);
        uploaded.push({
          url: result.url,
          publicId: result.publicId,
          name: file.name
        });
      }
      setUploadedImages((prev) => [...prev, ...uploaded]);
    } catch (err) {
      console.error('Upload failed:', err);
      setUploadError('Failed to upload one or more images. Please try again.');
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  const handleRemoveImage = (index) => {
    setUploadedImages((prev) => prev.filter((_, i) => i !== index));
  };

  /* ───────── Add / remove / edit magnets ───────── */
  const handleAddMagnet = () => {
    const hasContent =
      memoryTitle.trim() !== '' ||
      memoryStory.trim() !== '' ||
      uploadedImages.length > 0;

    if (hasContent) {
      const newMagnet = {
        id: Date.now(),
        title: memoryTitle.trim() || 'I Love you Mom',
        story:
          memoryStory.trim() ||
          'The Travel Memories collection features 6 carefully selected magnets, each with a beautiful image on the front and a meaningful story on the back. Perfect for decorating your fridge, office space, or as a thoughtful gift for someone special.',
        uploadedImages: [...uploadedImages]
      };
      setMagnets((prev) => [...prev, newMagnet]);

      setMemoryTitle('');
      setMemoryStory('');
      setUploadedImages([]);
      setUploadError('');
    }
  };

  const handleRemoveMagnet = (magnetId) => {
    setMagnets((prev) => prev.filter((magnet) => magnet.id !== magnetId));
  };

  const handleEditMagnet = (magnet) => {
    if (!magnet) return;
    setMemoryTitle(magnet.title || '');
    setMemoryStory(magnet.story || '');
    setUploadedImages(magnet.uploadedImages || []);
    handleRemoveMagnet(magnet.id);
  };

  const handleEditMagnetById = (magnetId) => {
    const magnetToEdit = magnets.find((m) => m.id === magnetId);
    if (magnetToEdit) handleEditMagnet(magnetToEdit);
  };

  const isContinueEnabled = true;

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[100] transition-opacity duration-300"
        onClick={onClose}
      />

      <div className="fixed right-0 top-0 h-full w-full md:w-[60%] lg:w-[50%] xl:w-[40%] bg-[#F9F6F2] z-[999] overflow-y-auto transition-transform duration-300 ease-in-out">
        <div className="h-full flex flex-col">
          <div className="flex items-center justify-between px-6 mt-6 pb-2 border-b-2 border-[#DBDBDB]">
            <div className="text-left">
              <h2 className="text-2xl font-bold text-[#1C2A46]">
                Customise Your Giftbox
              </h2>
              <p className="text-[#6B635D] text-sm mt-1">
                Make this Frame it Forward box personal to the one you love.
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-[#F8F8F8] border border-[#DBDBDB] rounded"
            >
              <CloseIcon className="w-6 h-6 text-[#1C2A46]" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4">
            <div className="mb-8">
              <h3 className="text-lg text-left mt-3 font-medium text-[#1C2A46] mb-4">
                Select Magnet Type
              </h3>
              <div className="space-y-4">
                <button
                  onClick={() => setSelectedMagnetType('pre-curated')}
                  className="flex justify-start gap-3 w-full text-left transition-colors"
                >
                  <div
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      selectedMagnetType === 'pre-curated'
                        ? 'border-[#1C2A46]'
                        : 'border-[#1C2A46]'
                    }`}
                  >
                    {selectedMagnetType === 'pre-curated' && (
                      <div className="w-3 h-3 bg-[#1C2A46] rounded-full"></div>
                    )}
                  </div>
                  <div>
                    <div className="font-medium text-[#1C2A46] mb-1">
                      Use Pre-curated Collections
                    </div>
                    <div className="text-sm text-[#6B635D]">
                      Four thoughtfully selected images from our themed
                      collections.
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => setSelectedMagnetType('upload-own')}
                  className="flex justify-start gap-3 w-full text-left transition-colors"
                >
                  <div
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      selectedMagnetType === 'upload-own'
                        ? 'border-[#1C2A46]'
                        : 'border-[#1C2A46]'
                    }`}
                  >
                    {selectedMagnetType === 'upload-own' && (
                      <div className="w-3 h-3 bg-[#1C2A46] rounded-full"></div>
                    )}
                  </div>
                  <div>
                    <div className="font-medium text-[#1C2A46] mb-1">
                      Upload My Own Photos & Stories
                    </div>
                    <div className="text-sm text-[#6B635D]">
                      Use your own images to personalise each magnet.
                    </div>
                  </div>
                </button>
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg text-left font-medium text-[#1C2A46] mb-4">
                How Many Magnet?
              </h3>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center justify-between space-x-4 w-36 p-1.5 border border-[#E0E0E0]">
                  <button
                    onClick={() =>
                      setMagnetSetCount((prev) => Math.max(1, prev - 1))
                    }
                    className="flex items-center justify-center hover:bg-[#F5F5F5] transition-colors"
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6 12h12"
                        stroke="#1C2A46"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                  <span className="text-base font-normal text-[#1C2A46] min-w-[3rem] text-center">
                    {magnetSetCount} set
                  </span>
                  <button
                    onClick={() => setMagnetSetCount((prev) => prev + 1)}
                    className="rounded-lg flex items-center justify-center hover:bg-[#F5F5F5] transition-colors"
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 6v12M6 12h12"
                        stroke="#1C2A46"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <p className="text-sm text-[#6B635D] italic text-left">
                Each set contains 4 memory magnets
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg text-left font-medium text-[#1C2A46] mb-4">
                How many frames?
              </h3>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center justify-between space-x-4 w-fit p-1.5 border border-[#E0E0E0]">
                  <button
                    onClick={() => setFrameCount((prev) => Math.max(1, prev - 1))}
                    className="flex items-center justify-center hover:bg-[#F5F5F5] transition-colors"
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6 12h12"
                        stroke="#1C2A46"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                  <span className="text-base font-normal text-[#1C2A46] min-w-[3rem] text-center">
                    {frameCount} frame
                  </span>
                  <button
                    onClick={() => setFrameCount((prev) => prev + 1)}
                    className="rounded-lg flex items-center justify-center hover:bg-[#F5F5F5] transition-colors"
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 6v12M6 12h12"
                        stroke="#1C2A46"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <p className="text-sm text-[#6B635D] italic text-left">
                Box includes 1 frame by default
              </p>
            </div>

            {selectedMagnetType === 'upload-own' && (
              <div className="mb-8">
                <h3 className="text-lg font-medium text-left text-[#1C2A46] mb-2">
                  Personalise Your Magnet
                </h3>
                <p className="text-[#6B635D] font-light mb-6 text-left">
                  Add your own photos for each magnet in the set. If you skip
                  this step, Kapta will curate a beautiful themed collection for
                  you.
                </p>

                {magnets.map((magnet, index) => (
                  <div
                    key={magnet.id}
                    className="flex gap-3 mb-6 border-b border-[#E4E4E4] pb-6"
                  >
                    <div className="overflow-hidden w-2/4">
                      {magnet.uploadedImages && magnet.uploadedImages.length > 0 ? (
                        <img
                          src={magnet.uploadedImages[0].url}
                          alt={`Magnet ${index + 1}`}
                          className="h-32 w-full object-cover"
                        />
                      ) : (
                        <img
                          src="https://www.realsimple.com/thmb/1nO0GmEuF87RSxFBmTWtfo6TZW0=/1500x0/filters:no_upscale():max_bytes(150000):strip_icc()/the-best-gift-giver-GettyImages-1706575747-2089a300e6594496b7f558585a4baefb.jpg"
                          alt="Sample magnet"
                          className="h-32 w-full object-cover"
                        />
                      )}
                    </div>
                    <div className="flex gap-9 justify-between w-3/4">
                      <div className="text-left flex-1">
                        <h3 className="text-xl font-bold text-[#1C2A46]">
                          {magnet.title || 'I Love you Mom'}
                        </h3>
                        <p className="text-[#6B635D] text-sm font-light leading-relaxed line-clamp-3">
                          {magnet.story ||
                            'The Travel Memories collection features 6 carefully selected magnets, each with a beautiful image on the front and a meaningful story on the back.'}
                        </p>
                      </div>

                      <div className="flex gap-3 items-end">
                        <button
                          onClick={() => handleEditMagnetById(magnet.id)}
                          className="text-[#1C2A46] border-b-2 border-[#1C2A46] font-medium pb-1 hover:text-[#C49660] hover:border-[#C49660] transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleRemoveMagnet(magnet.id)}
                          className="text-[#1C2A46] border-b-2 border-[#1C2A46] font-medium pb-1 hover:text-red-600 hover:border-red-600 transition-colors"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="border-t border-[#E4E4E4] pt-6">
                  <div className="flex gap-5 w-full">
                    <div className="mb-6 w-1/2">
                      <h4 className="text-base font-normal text-[#1C2A46] text-left mb-3">
                        Add Photo
                      </h4>
                      <div
                        className="bg-black h-72 w-52 flex flex-col justify-center items-center cursor-pointer hover:opacity-90 transition-opacity relative"
                        onClick={() => {
                          if (!isUploading) {
                            document.getElementById('photo-upload').click();
                          }
                        }}
                      >
                        {isUploading ? (
                          <div className="text-center">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white mx-auto"></div>
                            <p className="text-sm text-white mt-2">Uploading…</p>
                          </div>
                        ) : uploadedImages.length > 0 ? (
                          <div className="relative w-full h-full">
                            <img
                              src={uploadedImages[0].url}
                              alt="Uploaded preview"
                              className="w-full h-full object-cover"
                            />
                            <div
                              className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center cursor-pointer hover:bg-red-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveImage(0);
                              }}
                            >
                              ×
                            </div>
                            {uploadedImages.length > 1 && (
                              <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1 text-xs rounded">
                                +{uploadedImages.length - 1} more
                              </div>
                            )}
                          </div>
                        ) : (
                          <>
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                              <svg
                                width="32"
                                height="32"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M12 5v14M5 12h14"
                                  stroke="#1C2A46"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </div>
                            <p className="text-sm text-white mt-2 text-center">
                              PNG, JPG, JPEG <br />
                              (max size. 10mb)
                            </p>
                          </>
                        )}
                      </div>

                      <input
                        id="photo-upload"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleImageUpload}
                        multiple
                        disabled={isUploading}
                      />

                      {uploadError && (
                        <p className="mt-2 text-xs text-red-600 text-left">
                          {uploadError}
                        </p>
                      )}
                    </div>

                    <div className="w-1/2">
                      <div className="mb-4">
                        <h4 className="text-base font-normal text-[#1C2A46] text-left mb-3">
                          Memory Title (optional)
                        </h4>
                        <input
                          type="text"
                          placeholder='e.g. "For When You Need a Reminder"'
                          value={memoryTitle}
                          onChange={(e) => setMemoryTitle(e.target.value)}
                          className="w-full px-4 py-4 border border-[#E4E4E4] bg-[#F5F5F5] text-[#6B635D] focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent placeholder-[#6B635D] placeholder:font-light"
                        />
                      </div>

                      <div>
                        <h4 className="text-base font-normal text-[#1C2A46] text-left mb-3">
                          Memory Story (Back of magnet)
                        </h4>
                        <textarea
                          placeholder="Tell the story behind this moment. Who was there? What happened? And why does it matter?"
                          value={memoryStory}
                          onChange={(e) => {
                            if (e.target.value.length <= 2000) {
                              setMemoryStory(e.target.value);
                            }
                          }}
                          className="w-full px-4 py-4 border border-[#E4E4E4] bg-[#F5F5F5] text-[#6B635D] focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent placeholder-[#666666] placeholder:font-light h-40"
                          maxLength="2000"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end mt-4">
                    <button
                      onClick={handleAddMagnet}
                      disabled={
                        (!memoryStory.trim() &&
                          !memoryTitle.trim() &&
                          uploadedImages.length === 0) ||
                        isUploading
                      }
                      className={`w-32 border py-3 text-[#1C2A46] font-medium transition-colors ${
                        (!memoryStory.trim() &&
                          !memoryTitle.trim() &&
                          uploadedImages.length === 0) ||
                        isUploading
                          ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                          : 'border-[#DBDBDB] hover:border-[#D2A146]'
                      }`}
                    >
                      + Add another
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="p-6">
            <button
              onClick={handleContinue}
              disabled={!isContinueEnabled}
              className={`w-full py-3 font-medium transition-colors ${
                isContinueEnabled
                  ? 'bg-[#D2A146] text-white hover:bg-[#C49660]'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Save & Continue
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default GiftBoxCustomizeModal;